import { useEffect, useState } from "react";
import { X, Share, Plus } from "lucide-react";

// Session key so the prompt can be recalled by other flows if we ever
// need to (e.g. "Install app" button in the footer).
const DISMISSED_KEY = "mamamina-pwa-install-dismissed";
const DISMISSED_TTL_DAYS = 14;

function wasRecentlyDismissed() {
  try {
    const raw = localStorage.getItem(DISMISSED_KEY);
    if (!raw) return false;
    const ts = parseInt(raw, 10);
    if (!ts) return false;
    const days = (Date.now() - ts) / (1000 * 60 * 60 * 24);
    return days < DISMISSED_TTL_DAYS;
  } catch {
    return false;
  }
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function isIOS() {
  if (typeof navigator === "undefined") return false;
  return /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream;
}

/**
 * PwaInstallPrompt — dark, on-brand install nudge.
 *
 * Behaviour:
 *   - Waits 8s after page load to avoid interrupting the hero video.
 *   - On Chrome/Edge/Android, uses the `beforeinstallprompt` event and shows
 *     a proper "Install" button that fires the native install dialog.
 *   - On iOS Safari (no BIP event), shows a manual "Share ➜ Add to Home
 *     Screen" hint instead.
 *   - Auto-hides when the app is already installed / running standalone.
 *   - Dismissal remembered for 14 days.
 */
export default function PwaInstallPrompt() {
  const [visible, setVisible] = useState(false);
  const [deferred, setDeferred] = useState(null);
  const [mode, setMode] = useState("android"); // "android" | "ios"

  useEffect(() => {
    if (isStandalone() || wasRecentlyDismissed()) return;

    let timer = null;

    const onBip = (e) => {
      e.preventDefault();
      setDeferred(e);
      setMode("android");
      timer = setTimeout(() => setVisible(true), 8000);
    };

    window.addEventListener("beforeinstallprompt", onBip);

    // iOS fallback — no BIP event fires; show manual instructions instead.
    if (isIOS()) {
      timer = setTimeout(() => {
        setMode("ios");
        setVisible(true);
      }, 8000);
    }

    // Hide once the app is actually installed.
    const onInstalled = () => setVisible(false);
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBip);
      window.removeEventListener("appinstalled", onInstalled);
      if (timer) clearTimeout(timer);
    };
  }, []);

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISSED_KEY, String(Date.now()));
    } catch {
      /* private mode */
    }
    setVisible(false);
  };

  const install = async () => {
    if (!deferred) return;
    try {
      deferred.prompt();
      const { outcome } = await deferred.userChoice;
      if (outcome === "accepted") setVisible(false);
      else dismiss();
    } catch {
      dismiss();
    }
  };

  if (!visible) return null;

  return (
    <div
      data-testid="pwa-install-prompt"
      className="fixed left-1/2 -translate-x-1/2 z-[90] w-[min(94vw,420px)] bottom-4 sm:bottom-6 border border-[var(--mm-accent)]/45 bg-[var(--mm-charcoal)]/97 backdrop-blur-lg shadow-[0_18px_50px_-15px_rgba(0,0,0,0.3)] px-4 py-3.5"
    >
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss install prompt"
        data-testid="pwa-install-dismiss"
        className="absolute top-2 right-2 text-[var(--mm-cream)]/50 hover:text-[var(--mm-cream)] transition-colors p-1"
      >
        <X size={14} />
      </button>

      <div className="flex items-start gap-3">
        <img
          src="/media/logo-300.png"
          alt=""
          className="w-11 h-11 rounded-full border border-[var(--mm-accent)]/40 shrink-0"
        />
        <div className="pr-5 flex-1 min-w-0">
          <p className="text-[11px] tracking-[0.28em] uppercase text-[var(--mm-accent)] mb-1">
            Install Mamamina
          </p>
          {mode === "android" ? (
            <>
              <p className="text-sm text-[var(--mm-cream)] leading-snug mb-2.5">
                Get one-tap access to the menu, orders and loyalty stamps — no store required.
              </p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={install}
                  data-testid="pwa-install-accept"
                  className="bg-[var(--mm-accent)] text-[var(--mm-black)] text-xs tracking-[0.24em] uppercase px-4 py-2 hover:opacity-90 transition-opacity font-semibold"
                >
                  Install
                </button>
                <button
                  type="button"
                  onClick={dismiss}
                  data-testid="pwa-install-later"
                  className="text-[11px] tracking-[0.24em] uppercase text-[var(--mm-cream)]/60 hover:text-[var(--mm-cream)] transition-colors px-2 py-2"
                >
                  Later
                </button>
              </div>
            </>
          ) : (
            <p className="text-sm text-[var(--mm-cream)] leading-snug">
              To install, tap{" "}
              <Share size={12} className="inline mx-0.5 -mt-0.5 text-[var(--mm-accent)]" />{" "}
              then <span className="text-[var(--mm-accent)] font-medium inline-flex items-center gap-0.5">Add to Home Screen<Plus size={11} className="ml-0.5" /></span>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
